// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { getCurrentPosition, getCurrentTime } from '@components/Timeline/Timeline/utils';

// TYPES ---------------------------------------------------------------- //

interface TimelineMarker {
  time: string;
  label: string;
  color?: string;
}

interface TimelineProviderProps {
  componentAreaRow?: React.ComponentType<any> | null;
  componentAreaRowProps?: Record<string, any>;
  componentIndexRow?: React.ComponentType<any> | null;
  componentIndexRowProps?: Record<string, any>;
  children: React.ReactNode;
  data?: any[];
  date: string;
  header?: React.ReactNode;
  headerSize: number;
  intervalCallback?: () => void;
  markers?: TimelineMarker[];
  markerUpdateMs: number;
  onTimelineReady?: (scrollFn: (scrollPosition: number) => void) => void;
  rowSize: number;
  scrollKey: string;
  showMarkers: boolean;
  sidebarSize: number;
  timezoneOverride: string;
  zIndex: number;
  zoom: number;
}

interface TimelineContextType {
  // PROPS
  componentAreaRow?: React.ComponentType<any> | null;
  componentAreaRowProps?: Record<string, any>;
  componentIndexRow?: React.ComponentType<any> | null;
  componentIndexRowProps?: Record<string, any>;
  data?: any[];
  date: string;
  header?: React.ReactNode;
  headerSize: number;
  intervalCallback?: () => void;
  markers?: TimelineMarker[];
  markerUpdateMs: number;
  onTimelineReady?: (scrollFn: (scrollPosition: number) => void) => void;
  rowSize: number;
  scrollKey: string;
  showMarkers: boolean;
  sidebarSize: number;
  timezoneOverride: string;
  zIndex: number;
  zoom: number;

  // REFS
  timelineRef: React.RefObject<HTMLDivElement>;

  // STATE
  isCurrentDay: boolean;
  currentTime: string;
  currentPosition: number;

  isHovering: boolean;
  markerHoverTime: string | null;
  markerHoverPosition: number;

  // STATE HANDLERS
  setIsCurrentDay: (value: boolean) => void;
  setCurrentTime: (value: string) => void;
  setCurrentPosition: (value: number) => void;

  setIsHovering: (value: boolean) => void;
  setMarkerHoverTime: (value: string | null) => void;
  setMarkerHoverPosition: (value: number) => void;
}

// CONTEXT ---------------------------------------------------------------- //

const TimelineContext = React.createContext<TimelineContextType>({} as TimelineContextType);

// PROVIDER ---------------------------------------------------------------- //

const TimelineProvider = ({
  componentAreaRow,
  componentAreaRowProps,
  componentIndexRow,
  componentIndexRowProps,
  children,
  data,
  date,
  header,
  headerSize,
  intervalCallback,
  markers,
  markerUpdateMs,
  onTimelineReady,
  rowSize,
  scrollKey,
  showMarkers,
  sidebarSize,
  timezoneOverride,
  zIndex,
  zoom,
}: TimelineProviderProps) => {
  // REFS //
  const timelineRef = React.useRef<HTMLDivElement>(null);

  // STATE //
  const [isCurrentDay, setIsCurrentDay] = React.useState(true);
  const [currentTime, setCurrentTime] = React.useState(getCurrentTime(timezoneOverride));
  const [currentPosition, setCurrentPosition] = React.useState(getCurrentPosition(date, zoom, timezoneOverride));

  const [isHovering, setIsHovering] = React.useState(false);
  const [markerHoverTime, setMarkerHoverTime] = React.useState(null);
  const [markerHoverPosition, setMarkerHoverPosition] = React.useState(0);

  const context = {
    // PROPS //

    componentAreaRow,
    componentAreaRowProps,
    componentIndexRow,
    componentIndexRowProps,
    data,
    date,
    header,
    headerSize,
    intervalCallback,
    markers,
    markerUpdateMs,
    onTimelineReady,
    rowSize,
    scrollKey,
    showMarkers,
    sidebarSize,
    timezoneOverride,
    zIndex,
    zoom,

    // REFS //

    timelineRef,

    // STATE //

    isCurrentDay,
    currentTime,
    currentPosition,

    isHovering,
    markerHoverTime,
    markerHoverPosition,

    // STATE HANDLERS //

    setIsCurrentDay,
    setCurrentTime,
    setCurrentPosition,

    setIsHovering,
    setMarkerHoverTime,
    setMarkerHoverPosition,
  };

  return <TimelineContext.Provider value={context}>{children}</TimelineContext.Provider>;
};

// HOOK ---------------------------------------------------------------- //

const useTimeline = (): TimelineContextType => React.useContext(TimelineContext);

// EXPORT ---------------------------------------------------------------- //

export { useTimeline, TimelineProvider };
